'use client';

import { AnimatePresence, motion } from 'motion/react';
import React from 'react';

import { EmployeesSpotClient } from '@/globals/EmployeesSpot/ComponentClient';
import {
  CareerList,
  Employee,
  EmployeesSpot as EmployeesSpotType,
} from '@/payload-types';
import { cn } from '@/utils/cn';

import { EmployeeCard } from '../EmployeeCard';
import styles from './EmployeeList.module.scss';

type Props = {
  cards: Employee[];
  filtered?: boolean;
  employeeSpotContent: {
    employeesSpot: EmployeesSpotType | false;
    careers: CareerList | false;
  };
};

const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 },
};

export const EmployeeList: React.FC<Props> = ({
  cards,
  employeeSpotContent,
  filtered = false,
}) => {
  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <motion.div
      variants={listVariants}
      initial='hidden'
      whileInView='visible'
      viewport={{ once: true }}
      className={cn('container', styles.list)}
      layout='position'
    >
      <AnimatePresence>
        {cards.map((card, index) => {
          let adjustedIndex = index;
          if (!filtered && index > 7) {
            adjustedIndex += 1; // Shift forward after the 5th card since the spot takes a place
          }

          if (!filtered && index === 8) {
            return (
              <React.Fragment key={card.id}>
                <motion.div
                  key={card.id}
                  className={styles.card}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                  variants={cardVariants}
                  style={{ willChange: 'transform, opacity' }}
                  transition={{
                    bounce: 0,
                    ease: [0.625, 0.05, 0, 1],
                    duration: 0.5,
                  }}
                  layout
                >
                  <EmployeeCard {...card} index={adjustedIndex} />
                </motion.div>
                <motion.div
                  key={'employee-spot'}
                  className={styles.card}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                  variants={cardVariants}
                  style={{ willChange: 'transform, opacity' }}
                  transition={{
                    bounce: 0,
                    ease: [0.625, 0.05, 0, 1],
                    duration: 0.5,
                  }}
                  layout
                >
                  <EmployeesSpotClient
                    employeeSpotContent={employeeSpotContent}
                  />
                </motion.div>
              </React.Fragment>
            );
          }

          return (
            <motion.div
              key={card.id}
              className={styles.card}
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true }}
              variants={cardVariants}
              style={{ willChange: 'transform, opacity' }}
              transition={{
                bounce: 0,
                ease: [0.625, 0.05, 0, 1],
                duration: 0.5,
              }}
              layout
            >
              <EmployeeCard {...card} index={adjustedIndex} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
};
